/**
 * Concurrency cap for parallel external-API fan-outs.
 *
 * Fan-outs to Power Automate, Power Apps, and Microsoft Graph hit per-tenant
 * throttles long before they hit per-origin browser limits. Six in-flight
 * requests is the empirical sweet spot — large enough to mask network RTT,
 * small enough to stay below most upstream rate limits.
 *
 * On low-power devices we drop further — fewer cores means parsing /
 * decompressing many concurrent responses competes with the UI thread.
 */

/**
 * Returns the suggested concurrency cap for the current device.
 *
 * - Hard ceiling 6 (matches Microsoft's typical per-user throttle window
 *   and the common HTTP/1.1 per-origin connection limit).
 * - Floor 2 so even single-core / unknown devices still get parallelism.
 * - Scales with `navigator.hardwareConcurrency` between those bounds.
 */
export function getRecommendedConcurrency(): number {
    const cores = (typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency))
        ? navigator.hardwareConcurrency
        : 4;
    return Math.min(6, Math.max(2, cores));
}

export interface MapWithConcurrencyOptions {
    /** Maximum in-flight workers. Defaults to `getRecommendedConcurrency()`. */
    concurrency?: number;
    /** Optional progress callback fired after each item resolves. */
    onProgress?: (completed: number, total: number) => void;
}

/**
 * Run `worker` over `items` with at most `opts.concurrency` in flight at once.
 *
 * Semantics mirror `Promise.all`: if any worker rejects, the returned promise
 * rejects (other in-flight workers continue but their results are discarded).
 * If you want to collect per-item failures, wrap your worker body in a
 * try/catch and return a result envelope yourself.
 *
 * Returned array is in the same order as `items`.
 */
export async function mapWithConcurrency<T, R>(
    items: T[],
    worker: (item: T, index: number) => Promise<R>,
    opts: MapWithConcurrencyOptions = {}
): Promise<R[]> {
    const total = items.length;
    if (total === 0) return [];

    const concurrency = Math.max(1, opts.concurrency ?? getRecommendedConcurrency());
    const results: R[] = new Array(total);
    let cursor = 0;
    let completed = 0;

    const runner = async (): Promise<void> => {
        while (true) {
            const idx = cursor++;
            if (idx >= total) return;
            results[idx] = await worker(items[idx], idx);
            completed++;
            opts.onProgress?.(completed, total);
        }
    };

    const workerCount = Math.min(concurrency, total);
    await Promise.all(Array.from({ length: workerCount }, () => runner()));
    return results;
}
